/* Chrome Vector: texture-only melting. No page/content animation or scrolling override. */
(() => {
  'use strict';
  const host = document.querySelector('[data-cv-melting-background]');
  if (!host || host.dataset.initialized) return;
  host.dataset.initialized = 'true';
  const root = document.documentElement;
  const canvas = host.querySelector('canvas');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const editor = Boolean(window.Shopify && window.Shopify.designMode);
  const isHome = host.dataset.home === 'true';
  let heroEnd = 0, active = false, scrollPhase = 0, scrollTarget = 0;
  let gl, program, texture, buffer, uniforms, image;
  let started = false, loaded = false, failed = false, frame = 0, scrollFrame = 0;
  let elapsed = 0, lastTime = 0, imageAspect = 1;
  const vertexSource = `
    attribute vec2 a_position;
    varying vec2 v_uv;
    void main() {
      v_uv = (a_position + 1.0) * 0.5;
      gl_Position = vec4(a_position, 0.0, 1.0);
    }
  `;
  const fragmentSource = `
    precision mediump float;
    uniform sampler2D u_texture;
    uniform vec2 u_cover;
    uniform float u_aspect;
    uniform float u_time;
    uniform float u_scroll;
    varying vec2 v_uv;
    void main() {
      vec2 p = v_uv;
      vec2 q = p * vec2(u_aspect, 1.0);
      float t = u_time * 0.19 + u_scroll;
      // Broad, continuous folds rather than tiled noise or a sliding photo.
      float a = sin(q.y * 5.0 + sin(q.x * 2.8 - t * 0.7) + t);
      float b = cos(q.x * 4.0 + sin(q.y * 3.6 + t * 0.55) - t * 0.65);
      vec2 melt = vec2(a * 0.036, b * 0.024);
      melt.x += sin(q.y * 9.0 - t * 0.45) * 0.008;
      melt.y += sin(q.x * 6.0 + q.y * 3.0 - t * 0.6) * 0.009;
      // Overscan ensures the flowing surface never exposes an empty edge.
      vec2 uv = (p - 0.5 + melt) * u_cover * 0.88 + 0.5;
      gl_FragColor = vec4(texture2D(u_texture, uv).rgb, 1.0);
    }
  `;
  function stop() {
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    lastTime = 0;
  }
  function fallback() {
    failed = true;
    loaded = false;
    host.classList.remove('cv-melting-ready');
    stop();
    if (gl && !gl.isContextLost()) {
      if (texture) gl.deleteTexture(texture);
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
    }
  }
  function compile(kind, source) {
    const shader = gl.createShader(kind);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      throw new Error('Background shader unavailable');
    }
    return shader;
  }
  function resize() {
    if (!loaded || failed) return;
    const width = Math.max(1, host.clientWidth);
    const height = Math.max(1, host.clientHeight);
    // Bounded GPU work on phones and high-DPI/large screens.
    const ratio = Math.min(window.devicePixelRatio || 1, 1.25, 1440 / width, 1440 / height);
    canvas.width = Math.max(1, Math.round(width * ratio));
    canvas.height = Math.max(1, Math.round(height * ratio));
    gl.viewport(0, 0, canvas.width, canvas.height);
    const aspect = width / height;
    gl.uniform1f(uniforms.aspect, aspect);
    gl.uniform2f(uniforms.cover, Math.min(aspect / imageAspect, 1), Math.min(imageAspect / aspect, 1));
  }
  /* The entrance overlay is opaque and covers the viewport, so anything this
     module draws underneath it is invisible work — and it is a second WebGL
     context competing with the badge for the GPU at the worst possible moment. */
  /* 2.2MB png vs ~105KB webp for a texture that gets domain-warped into folds
     before anyone sees a pixel of it. The png stays for browsers without webp. */
  const supportsWebP = (() => {
    try {
      const c = document.createElement('canvas');
      return c.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } catch (_) { return false; }
  })();
  function textureURL() {
    return (supportsWebP && host.dataset.textureWebp) || host.dataset.texture;
  }

  function entranceUp() {
    return !!document.querySelector('[data-cv-entrance]') &&
      !root.classList.contains('cv-entrance-complete');
  }

  function start() {
    if (started || failed) return;
    started = true;
    /* Deferred from the Liquid template: this is where the texture is first
       genuinely needed, rather than during first paint. */
    if (!host.style.backgroundImage && textureURL()) {
      host.style.backgroundImage = "url('" + textureURL() + "')";
    }
    try {
      gl = canvas.getContext('webgl', {
        alpha:false, antialias:false, depth:false, stencil:false,
        powerPreference:'low-power', preserveDrawingBuffer:false
      });
      if (!gl) { fallback(); return; }
      program = gl.createProgram();
      const vertex = compile(gl.VERTEX_SHADER, vertexSource);
      const fragment = compile(gl.FRAGMENT_SHADER, fragmentSource);
      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Background program unavailable');
      gl.useProgram(program);
      buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
      const position = gl.getAttribLocation(program, 'a_position');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      uniforms = {
        cover:gl.getUniformLocation(program,'u_cover'),
        aspect:gl.getUniformLocation(program,'u_aspect'),
        time:gl.getUniformLocation(program,'u_time'),
        scroll:gl.getUniformLocation(program,'u_scroll')
      };
      gl.uniform1i(gl.getUniformLocation(program,'u_texture'), 0);
      texture = gl.createTexture();
      image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        if (failed) return;
        try {
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, texture);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          if (gl.getError() !== gl.NO_ERROR) throw new Error('Texture unavailable');
          imageAspect = image.naturalWidth / image.naturalHeight;
          loaded = true;
          resize();
          sync();
        } catch (_) { fallback(); }
      };
      // The CSS texture remains visible if CORS, a CDN request, or WebGL fails.
      image.onerror = fallback;
      image.src = textureURL();
    } catch (_) { fallback(); }
  }
  function tick(now) {
    frame = 0;
    if (!active || reduced.matches || editor || document.hidden || failed || !loaded) return;
    if (!lastTime || now - lastTime >= 1000 / 30) {
      elapsed += lastTime ? Math.min(now - lastTime, 100) / 1000 : 0;
      lastTime = now;
      // Ease changes in scroll phase; never reset the image at a modulo boundary.
      scrollPhase += (scrollTarget - scrollPhase) * 0.08;
      gl.uniform1f(uniforms.time, elapsed);
      gl.uniform1f(uniforms.scroll, scrollPhase);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      host.classList.add('cv-melting-ready');
    }
    frame = window.requestAnimationFrame(tick);
  }
  function sync() {
    if (!active || document.hidden || reduced.matches || editor || failed) {
      stop();
      if (reduced.matches || editor) host.classList.remove('cv-melting-ready');
      return;
    }
    start();
    if (loaded && !frame) frame = window.requestAnimationFrame(tick);
  }
  function updateScroll() {
    scrollFrame = 0;
    const y = window.scrollY || window.pageYOffset || 0;
    active = (!isHome || y >= heroEnd) && !entranceUp();
    root.classList.toggle('cv-liquid-active', active);
    scrollTarget = Math.max(0, y - heroEnd) * 0.00012;
    sync();
  }
  function requestScroll() {
    if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateScroll);
  }
  function measure() {
    const hero = isHome ? document.querySelector('#MainContent .hero') : null;
    heroEnd = hero ? hero.getBoundingClientRect().bottom + (window.scrollY || 0) - 2 : 0;
    resize();
    updateScroll();
  }
  canvas.addEventListener('webglcontextlost', event => {
    event.preventDefault();
    fallback();
  });
  document.addEventListener('visibilitychange', sync);
  window.addEventListener('scroll', requestScroll, {passive:true});
  window.addEventListener('resize', measure, {passive:true});
  window.addEventListener('load', measure, {once:true});
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => {
    document.addEventListener(name, () => window.requestAnimationFrame(measure));
  });
  if (reduced.addEventListener) reduced.addEventListener('change', sync);
  else reduced.addListener(sync);
  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', measure);
  /* Once the lobby is gone this backdrop is allowed to wake up. */
  if (entranceUp() && window.MutationObserver) {
    const watch = new MutationObserver(() => {
      if (!entranceUp()) { watch.disconnect(); measure(); }
    });
    watch.observe(root, {attributes:true, attributeFilter:['class']});
    watch.observe(document.body, {childList:true});
  }
  measure();
})();
